
import React from "react";
import { FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Textarea } from "@/components/ui/textarea";
import { UseFormReturn } from "react-hook-form";
import { JournalFormValues } from "@/components/journal/form/JournalFormSchema";
import HelpTooltip from "@/components/help/HelpTooltip";

interface StuckPointFieldProps {
  form: UseFormReturn<JournalFormValues>;
  onUserStartedTyping?: () => void;
}

const StuckPointField: React.FC<StuckPointFieldProps> = ({ form, onUserStartedTyping }) => {
  const handleChange = (value: string) => {
    // Call the field onChange to update form state
    form.setValue("stuck_point", value, { shouldValidate: true });
    
    // Notify parent that user started typing
    if (onUserStartedTyping && value.length > 0) {
      onUserStartedTyping();
    }
  };

  return (
    <div data-tutorial="stuck-point-field">
      <FormField
        control={form.control}
        name="stuck_point"
        render={({ field }) => (
          <FormItem>
            <FormLabel id="stuck-point-label" className="flex items-center gap-2">
              What are you currently stuck on?
              <HelpTooltip 
                content="Describe any challenge, decision, or situation where you feel blocked. It could be work-related, personal, creative, or anything where you're unsure how to move forward."
                side="right"
              />
            </FormLabel>
            <FormDescription id="stuck-point-description">
              Describe the situation, challenge, or problem you're facing. This will help guide your journaling session.
            </FormDescription>
            <FormControl>
              <Textarea
                placeholder="Examples: 'I can't decide between two job offers' or 'I'm overwhelmed by my project deadline' or 'I don't know how to have this difficult conversation'"
                className="min-h-20"
                value={field.value}
                onChange={(e) => {
                  field.onChange(e);
                  handleChange(e.target.value);
                }}
                onBlur={field.onBlur}
                name={field.name}
                ref={field.ref}
                aria-labelledby="stuck-point-label"
                aria-describedby="stuck-point-description stuck-point-error"
                aria-invalid={!!form.formState.errors.stuck_point}
              />
            </FormControl>
            <FormMessage id="stuck-point-error" />
          </FormItem>
        )}
      />
    </div>
  );
};

export default StuckPointField;
